// Harness (JuanDzFix) — placar fase×verba: ONDE o pipeline erra, quanto custa,
// e qual célula atacar agora. Fonte: GET /harness (lê os JSON do cron 05:30 UTC
// em eval/jfix/out/). Escolher uma célula abre uma FRENTE (POST /harness/frentes);
// o andamento da frente é editado na seção Frentes (PATCH).
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Title, Text, Card, Badge } from '@tremor/react';
import { Link } from '@tanstack/react-router';
import { Hammer, X, Copy, Check } from 'lucide-react';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage } from '@/lib/api';

// ----------------------------------------------------------------------------- tipos
interface Exemplo { pid: number; verba_txt: string; fase: string; erro: string }
interface Lastro { decisorio?: number; outra_peca?: number; sem_trecho?: number }
interface Celula {
  fase: string; verba: string; casos: number; rs_delta: number; rs_lastro: number;
  score: number; detectado_por: string; sev: Record<string, number>;
  pids: number[]; n_pids_total: number; smell_candidato: boolean;
  frente_id: string | null; frente_status: string | null; exemplos: Exemplo[];
  // [08/10] lastro do trecho que o juiz citou (routers/harness.py): quantos
  // trechos vieram do DISPOSITIVO (decisorio), de outra peça, ou sem trecho.
  lastro: Lastro; rs_delta_todos: number; casos_decisorio: number;
  // [09/10 RANK] score_casos = casos com lastro decisório × severidade do juiz
  // (alta 3 / média 2 / baixa 1). Virou a ordem default porque a auditoria de 09/10
  // mostrou o R$Δ incerto na maioria dos defeitos (o R$ citado era o total da NOSSA
  // planilha, não a diferença) — dinheiro sozinho não ordena a fila.
  score_casos: number; sev_decisorio: Record<string, number>;
  rs_delta_incerto: number; n_delta: number;
}
interface Smell {
  regra: string; estagio: string; verba: string; n_casos: number;
  r_delta: number; r_lastro: number; detectado_por: string; condicao: string; casos: number[];
}
interface Frente {
  id: string; celula: { fase: string; verba: string }; status: string;
  pid: number | null; flag: string | null; nota: string | null;
  criado_em: string; atualizado_em: string; criado_por?: string | null;
}
interface HarnessData {
  gerado_em: string;
  placar: {
    celulas: Celula[]; dias: number | null; n_casos_sinal: number | null; n_celulas: number | null;
    sem_diagnostico: number[]; n_sem_diagnostico: number; arquivo_gerado_em: string | null;
    fonte: string | null; erro: string | null;
    lastro_juiz: Lastro; r_delta_decisorio: number | null; r_delta_todos: number | null;
  };
  smells: { minimo: number | null; n: number | null; candidatas: Smell[]; arquivo_gerado_em: string | null; fonte: string | null; erro: string | null };
  flags: {
    dias: number | null; n_flags: number | null;
    contagens: { vigente: number; inerte: number; fora_de_vigor: number; nao_observavel: number };
    fora_de_vigor: string[]; inertes: string[]; n_inertes: number; servicos: string[];
    arquivo_gerado_em: string | null; fonte: string | null; erro: string | null;
  };
  frentes: Frente[];
  status_validos: string[];
}

type Ordem = 'score_casos' | 'score' | 'rs_delta' | 'casos';
// [08/10] escopo do score: 'decisorio' (default) conta só o R$Δ cujo trecho saiu do
// dispositivo da sentença; 'todos' volta ao que o placar somava antes (inclui
// trecho de outra peça / sem trecho = artefato de leitura, não erro provado).
type Escopo = 'decisorio' | 'todos';

// ----------------------------------------------------------------------------- utils
const fmtInt = (n: number | null | undefined) => (n == null ? '—' : new Intl.NumberFormat('pt-BR').format(n));
const fmtBRL = (v: number | null | undefined) =>
  v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const fmtScore = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toFixed(0));
// R$Δ e score sob o escopo escolhido. Sem Δ a célula cai no lastro (exposição factual).
const deltaDe = (c: Celula, e: Escopo) => (e === 'todos' ? c.rs_delta_todos || c.rs_delta : c.rs_delta);
const scoreDe = (c: Celula, e: Escopo) => (deltaDe(c, e) || c.rs_lastro) * c.casos;
// ⚠️ a célula fica frágil quando a MAIORIA dos casos não tem trecho decisório:
// o R$Δ decisório descreve menos da metade dos casos que a linha agrega.
const poucoDecisorio = (c: Celula) => c.casos > 0 && c.casos_decisorio < c.casos * 0.5;
// ⚠️ R$Δ sem crédito: mais defeitos com R$ AMBÍGUO do que com Δ atribuído ⇒ a coluna
// R$Δ da linha não é dinheiro provado e a célula tem de ser lida por casos×severidade.
const deltaIncertoDomina = (c: Celula) => (c.rs_delta_incerto || 0) > (c.n_delta || 0);

const STATUS_COR: Record<string, string> = {
  candidata: 'gray', escolhida: 'blue', em_ciclo: 'indigo', shadow: 'amber',
  flip_pendente: 'orange', vigente: 'emerald', descartada: 'gray',
};

function CopyBtn({ texto }: { texto: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(texto).then(() => { setOk(true); setTimeout(() => setOk(false), 1200); });
      }}
      className="inline-flex shrink-0 items-center gap-1 rounded border border-gray-300 dark:border-gray-600 px-1.5 py-0.5 text-[11px] text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
      title="copiar"
    >
      {ok ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}{ok ? 'copiado' : 'copiar'}
    </button>
  );
}

function Contador({ label, valor, tom, hint }: { label: string; valor: number; tom: string; hint?: string }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${tom}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-xl font-bold tabular-nums leading-tight">{fmtInt(valor)}</div>
      {hint ? <div className="text-[11px] opacity-80">{hint}</div> : null}
    </div>
  );
}

// ----------------------------------------------------------------------------- painel lateral
function PainelCelula({
  c, onFechar, onEscolher, criando,
}: { c: Celula; onFechar: () => void; onEscolher: (c: Celula) => void; criando: boolean }) {
  const cmdRun = (pid: number) => `cd /opt/calcfin && venv/bin/python3.12 -m eval.jfix.jfix run ${pid}`;
  const cmdCiclo = (pid: number) => `cd /opt/calcfin && venv/bin/python3.12 -m eval.jfix.jfix ciclo ${pid} --flag <FLAG>`;
  return (
    <div className="fixed inset-y-0 right-0 z-40 w-full max-w-[520px] overflow-y-auto border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-2xl">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{c.fase}</div>
          <div className="truncate text-[15px] font-bold text-gray-900 dark:text-gray-50">{c.verba}</div>
        </div>
        <button type="button" onClick={onFechar} className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700" title="fechar">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-[13px]">
        <div className="rounded border border-gray-200 dark:border-gray-700 px-2 py-1">
          <div className="text-[11px] text-gray-500 dark:text-gray-400">casos</div>
          <div className="font-bold tabular-nums text-gray-900 dark:text-gray-50">{fmtInt(c.casos)}</div>
        </div>
        <div className="rounded border border-gray-200 dark:border-gray-700 px-2 py-1">
          <div className="text-[11px] text-gray-500 dark:text-gray-400">R$Δ (juiz)</div>
          <div className="font-bold tabular-nums text-gray-900 dark:text-gray-50">{fmtBRL(c.rs_delta)}</div>
        </div>
        <div className="rounded border border-gray-200 dark:border-gray-700 px-2 py-1">
          <div className="text-[11px] text-gray-500 dark:text-gray-400">R$ lastro</div>
          <div className="font-bold tabular-nums text-gray-900 dark:text-gray-50">{fmtBRL(c.rs_lastro)}</div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px] text-gray-700 dark:text-gray-200">
        <span>detectado por <b>{c.detectado_por || '—'}</b></span>
        {c.smell_candidato ? <Badge color="violet" size="xs">smell ≥3</Badge> : null}
        {c.frente_id ? <Badge color={STATUS_COR[c.frente_status || ''] || 'blue'} size="xs">frente: {c.frente_status}</Badge> : null}
      </div>

      <button
        type="button"
        disabled={criando || !!c.frente_id}
        onClick={() => onEscolher(c)}
        className="mt-3 w-full rounded bg-gray-900 dark:bg-gray-100 px-3 py-2 text-[13px] font-semibold text-white dark:text-gray-900 disabled:opacity-50"
      >
        {c.frente_id ? 'já existe frente para esta célula' : criando ? 'abrindo…' : 'Escolher como frente'}
      </button>

      <div className="mt-4 text-[12px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        casos ({c.pids.length} de {fmtInt(c.n_pids_total)})
      </div>
      <div className="mt-1 space-y-2">
        {c.pids.map((pid) => (
          <div key={pid} className="rounded border border-gray-200 dark:border-gray-700 p-2">
            <div className="flex items-center justify-between gap-2">
              <Link to="/cases/$caseId" params={{ caseId: String(pid) }} className="text-[13px] font-semibold text-blue-700 dark:text-blue-300 hover:underline">
                caso {pid}
              </Link>
            </div>
            <div className="mt-1 space-y-1">
              <div className="flex items-center gap-1.5">
                <code className="min-w-0 flex-1 truncate rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[11px] text-gray-800 dark:text-gray-100">{cmdRun(pid)}</code>
                <CopyBtn texto={cmdRun(pid)} />
              </div>
              <div className="flex items-center gap-1.5">
                <code className="min-w-0 flex-1 truncate rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[11px] text-gray-800 dark:text-gray-100">{cmdCiclo(pid)}</code>
                <CopyBtn texto={cmdCiclo(pid)} />
              </div>
            </div>
          </div>
        ))}
        {c.pids.length === 0 ? <Text className="text-[12px] text-gray-500">sem pids na célula</Text> : null}
      </div>

      {c.exemplos.length ? (
        <>
          <div className="mt-4 text-[12px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">o que o juiz disse</div>
          <div className="mt-1 space-y-2">
            {c.exemplos.map((e, i) => (
              <div key={`${e.pid}-${i}`} className="rounded border border-gray-200 dark:border-gray-700 p-2 text-[12px]">
                <div className="font-semibold text-gray-900 dark:text-gray-50">{e.pid} — {e.verba_txt}</div>
                <div className="text-gray-600 dark:text-gray-300">{e.fase}</div>
                <div className="mt-1 text-gray-800 dark:text-gray-100">{e.erro}</div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------------------- página
export function HarnessPage() {
  const qc = useQueryClient();
  const [ordem, setOrdem] = useState<Ordem>('score_casos');
  const [sel, setSel] = useState<Celula | null>(null);
  const [soSmell, setSoSmell] = useState(false);
  const [escopo, setEscopo] = useState<Escopo>('decisorio');

  const q = useQuery<HarnessData>({
    queryKey: ['harness'],
    queryFn: async () => (await api().get('/harness')).data,
    staleTime: 5 * 60_000,
  });

  const criar = useMutation({
    mutationFn: async (c: Celula) => (await api().post('/harness/frentes', { fase: c.fase, verba: c.verba })).data,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['harness'] }); setSel(null); },
  });
  const patch = useMutation({
    mutationFn: async (v: { id: string; campos: Partial<Frente> }) =>
      (await api().patch(`/harness/frentes/${v.id}`, v.campos)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['harness'] }),
  });

  const celulas = useMemo(() => {
    const base = (q.data?.placar.celulas || []).filter((c) => (soSmell ? c.smell_candidato : true));
    const k: Record<Ordem, (c: Celula) => number> = {
      score_casos: (c) => c.score_casos || 0,
      score: (c) => scoreDe(c, escopo), rs_delta: (c) => deltaDe(c, escopo) || c.rs_lastro, casos: (c) => c.casos,
    };
    return [...base].sort((a, b) => k[ordem](b) - k[ordem](a));
  }, [q.data, ordem, soSmell, escopo]);

  if (q.isLoading) return <LoadingSkeleton />;
  if (q.isError || !q.data) {
    return <Card><Text className="text-red-600 dark:text-red-400">Erro ao carregar o harness: {extractErrorMessage(q.error, 'falha na consulta')}</Text></Card>;
  }
  const d = q.data;
  const fl = d.flags.contagens;

  return (
    <div className="space-y-4 text-[13px]">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <Title className="flex items-center gap-2 text-gray-900 dark:text-gray-50">
            <Hammer className="h-5 w-5" /> JuanDz2
          </Title>
          <Text className="text-[12px] text-gray-600 dark:text-gray-300">
            Placar fase×verba dos últimos {d.placar.dias ?? '—'}d: {fmtInt(d.placar.n_casos_sinal)} casos com sinal de erro,{' '}
            {fmtInt(d.placar.n_celulas)} células. Gerado pelo cron 05:30 UTC — placar de{' '}
            <b>{d.placar.arquivo_gerado_em || '—'}</b>.
          </Text>
        </div>
      </div>

      {d.placar.erro ? (
        <Card><Text className="text-[12px] text-amber-700 dark:text-amber-300">placar indisponível: {d.placar.erro}</Text></Card>
      ) : null}

      {/* ---------------------------------------------------------------- flags */}
      <Card>
        <div className="flex items-baseline justify-between gap-2">
          <Text className="font-semibold text-gray-900 dark:text-gray-50">Flags — vigência (janela {d.flags.dias ?? '—'}d)</Text>
          <Text className="text-[11px] text-gray-500 dark:text-gray-400">{d.flags.arquivo_gerado_em || '—'} · {fmtInt(d.flags.n_flags)} flags `=1` no .env</Text>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Contador label="vigente" valor={fl.vigente} tom="border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-200" hint="em vigor + disparou" />
          <Contador label="inerte" valor={fl.inerte} tom="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200" hint="0 disparos na janela" />
          <Contador label="fora de vigor" valor={fl.fora_de_vigor} tom="border-red-300 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200" hint="=1 no .env, não no environ" />
          <Contador label="não observável" valor={fl.nao_observavel} tom="border-gray-300 bg-gray-50 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" hint="não imprime o próprio nome" />
        </div>
        {d.flags.fora_de_vigor.length ? (
          <div className="mt-3">
            <div className="text-[12px] font-semibold text-red-700 dark:text-red-300">fora de vigor ({d.flags.fora_de_vigor.length}) — sempre defeito: restart não aconteceu ou o unit não lê o .env</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {d.flags.fora_de_vigor.map((f) => (
                <code key={f} className="rounded bg-red-50 dark:bg-red-950 px-1.5 py-0.5 text-[11px] text-red-800 dark:text-red-200">{f}</code>
              ))}
            </div>
          </div>
        ) : null}
        {d.flags.inertes.length ? (
          <div className="mt-3">
            <div className="text-[12px] font-semibold text-amber-700 dark:text-amber-300">
              inertes (top {d.flags.inertes.length} de {fmtInt(d.flags.n_inertes)}) — flag de caso raro pode não disparar na janela
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {d.flags.inertes.map((f) => (
                <code key={f} className="rounded bg-amber-50 dark:bg-amber-950 px-1.5 py-0.5 text-[11px] text-amber-800 dark:text-amber-200">{f}</code>
              ))}
            </div>
          </div>
        ) : null}
      </Card>

      {/* ---------------------------------------------------------------- placar */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Text className="font-semibold text-gray-900 dark:text-gray-50">Placar fase×verba — clique na linha para abrir a célula</Text>
          <div className="flex items-center gap-2 text-[12px]">
            <label className="flex items-center gap-1 text-gray-700 dark:text-gray-200">
              <input type="checkbox" checked={soSmell} onChange={(e) => setSoSmell(e.target.checked)} /> só smells
            </label>
            <span className="text-gray-500 dark:text-gray-400">score</span>
            <select
              value={escopo}
              onChange={(e) => setEscopo(e.target.value as Escopo)}
              className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-0.5 text-[12px] text-gray-900 dark:text-gray-50"
            >
              <option value="decisorio">só decisório</option>
              <option value="todos">todos os trechos</option>
            </select>
            <span className="text-gray-500 dark:text-gray-400">ordenar por</span>
            <select
              value={ordem}
              onChange={(e) => setOrdem(e.target.value as Ordem)}
              className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-0.5 text-[12px] text-gray-900 dark:text-gray-50"
            >
              <option value="score_casos">casos × severidade</option>
              <option value="score">score (R$Δ × casos)</option>
              <option value="rs_delta">R$Δ</option>
              <option value="casos">casos</option>
            </select>
          </div>
        </div>
        <Text className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          R$Δ = valor que o JUIZ citou no defeito. R$lastro = exposição factual da verba (não é Δ). Sem gabarito de perito não existe Δ:
          Ordem default = <b>casos × severidade</b> (casos com trecho decisório pesados por alta 3 / média 2 / baixa 1): quando o R$Δ é incerto a coluna R$Δ mostra <b>incerto</b> e só a evidência ordena. {fmtInt(d.placar.n_sem_diagnostico)} casos com sinal mas sem judge_defeito ficam fora do placar.
          <br />
          <b>lastro</b> = de onde saiu o trecho que o juiz citou: <b>decisório</b> (dispositivo da sentença — é o que prova defeito),{' '}
          <b>outra peça</b> e <b>sem trecho</b> (artefato da nossa leitura, não erro provado). O score{' '}
          <b>{escopo === 'decisorio' ? 'só decisório' : 'todos os trechos'}</b> usa{' '}
          {escopo === 'decisorio' ? 'apenas o R$Δ com trecho do dispositivo' : 'o R$Δ de qualquer trecho (o que o placar somava antes)'}.
          Na janela: {fmtInt(d.placar.lastro_juiz?.decisorio)} decisório · {fmtInt(d.placar.lastro_juiz?.outra_peca)} outra peça ·{' '}
          {fmtInt(d.placar.lastro_juiz?.sem_trecho)} sem trecho. ⚠️ na linha = menos da metade dos casos tem trecho decisório.
        </Text>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-2">#</th>
                <th className="py-1 pr-2">fase do pipeline</th>
                <th className="py-1 pr-2">verba</th>
                <th className="py-1 pr-2 text-right">casos</th>
                <th className="py-1 pr-2 text-right">R$Δ</th>
                <th className="py-1 pr-2 text-right">R$lastro</th>
                <th className="py-1 pr-2">lastro (dec/outra/sem)</th>
                <th className="py-1 pr-2 text-right">casos×sev</th>
                <th className="py-1 pr-2 text-right">score</th>
                <th className="py-1 pr-2">detectado_por</th>
                <th className="py-1 pr-2">sinal</th>
              </tr>
            </thead>
            <tbody>
              {celulas.map((c, i) => (
                <tr
                  key={`${c.fase}|${c.verba}`}
                  onClick={() => setSel(c)}
                  className="cursor-pointer border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <td className="py-1 pr-2 tabular-nums text-gray-500 dark:text-gray-400">{i + 1}</td>
                  <td className="py-1 pr-2 text-gray-900 dark:text-gray-50">{c.fase}</td>
                  <td className="py-1 pr-2"><code className="text-[12px] text-gray-900 dark:text-gray-50">{c.verba}</code></td>
                  <td className="py-1 pr-2 text-right font-semibold tabular-nums text-gray-900 dark:text-gray-50">{fmtInt(c.casos)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums text-gray-900 dark:text-gray-50">
                    {deltaIncertoDomina(c) ? (
                      <span
                        title={`${c.rs_delta_incerto} defeitos com R$ no texto mas sem Δ atribuível contra ${c.n_delta} com Δ — o R$ citado era total de rubrica / valor da causa, não a diferença`}
                        className="text-amber-700 dark:text-amber-300"
                      >
                        incerto{deltaDe(c, escopo) ? ` (${fmtBRL(deltaDe(c, escopo))})` : ''}
                      </span>
                    ) : deltaDe(c, escopo) ? (
                      fmtBRL(deltaDe(c, escopo))
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="py-1 pr-2 text-right tabular-nums text-gray-600 dark:text-gray-300">{c.rs_lastro ? fmtBRL(c.rs_lastro) : '—'}</td>
                  <td className="py-1 pr-2 whitespace-nowrap tabular-nums">
                    <span className="font-semibold text-emerald-700 dark:text-emerald-300">{fmtInt(c.lastro?.decisorio || 0)}</span>
                    <span className="text-gray-400 dark:text-gray-500"> / </span>
                    <span className="text-amber-700 dark:text-amber-300">{fmtInt(c.lastro?.outra_peca || 0)}</span>
                    <span className="text-gray-400 dark:text-gray-500"> / </span>
                    <span className="text-gray-500 dark:text-gray-400">{fmtInt(c.lastro?.sem_trecho || 0)}</span>
                  </td>
                  <td
                    className="py-1 pr-2 text-right font-bold tabular-nums text-gray-900 dark:text-gray-50"
                    title={Object.entries(c.sev_decisorio || {}).map(([k2, v2]) => `${k2}:${v2}`).join(' ')}
                  >
                    {fmtInt(c.score_casos || 0)}
                  </td>
                  <td className="py-1 pr-2 text-right tabular-nums text-gray-600 dark:text-gray-300">{fmtScore(scoreDe(c, escopo))}</td>
                  <td className="py-1 pr-2 text-gray-700 dark:text-gray-200">{c.detectado_por || '—'}</td>
                  <td className="py-1 pr-2">
                    <span className="flex flex-wrap items-center gap-1">
                      {poucoDecisorio(c) ? (
                        <span
                          title={`só ${c.casos_decisorio} de ${c.casos} casos têm trecho do dispositivo — o R$Δ decisório descreve menos da metade da linha`}
                          className="rounded bg-amber-50 dark:bg-amber-950 px-1 py-0.5 text-[11px] text-amber-800 dark:text-amber-200"
                        >
                          ⚠️ {c.casos_decisorio}/{c.casos} dec.
                        </span>
                      ) : null}
                      {c.smell_candidato ? <Badge color="violet" size="xs">smell</Badge> : null}
                      {c.frente_id ? <Badge color={STATUS_COR[c.frente_status || ''] || 'blue'} size="xs">{c.frente_status}</Badge> : null}
                    </span>
                  </td>
                </tr>
              ))}
              {celulas.length === 0 ? (
                <tr><td colSpan={10} className="py-3 text-center text-gray-500 dark:text-gray-400">nenhuma célula</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ---------------------------------------------------------------- frentes */}
      <Card>
        <div className="flex items-baseline justify-between gap-2">
          <Text className="font-semibold text-gray-900 dark:text-gray-50">Frentes ({d.frentes.length})</Text>
          <Text className="text-[11px] text-gray-500 dark:text-gray-400">candidata → escolhida → em_ciclo → shadow → flip_pendente → vigente | descartada</Text>
        </div>
        {patch.isError ? <Text className="mt-1 text-[12px] text-red-600 dark:text-red-400">falha ao salvar: {extractErrorMessage(patch.error, 'erro')}</Text> : null}
        <div className="mt-2 space-y-2">
          {d.frentes.map((f) => (
            <div key={f.id} className="rounded border border-gray-200 dark:border-gray-700 p-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge color={STATUS_COR[f.status] || 'gray'} size="xs">{f.status}</Badge>
                <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{f.celula.fase} × <code>{f.celula.verba}</code></span>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">#{f.id} · criada {f.criado_em} · atualizada {f.atualizado_em}</span>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[140px_180px_1fr]">
                <select
                  value={f.status}
                  onChange={(e) => patch.mutate({ id: f.id, campos: { status: e.target.value } })}
                  className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-1 text-[12px] text-gray-900 dark:text-gray-50"
                >
                  {d.status_validos.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input
                  defaultValue={f.pid ?? ''}
                  placeholder="pid"
                  inputMode="numeric"
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    const n = v === '' ? null : Number(v);
                    if (n !== (f.pid ?? null) && (n === null || Number.isFinite(n))) patch.mutate({ id: f.id, campos: { pid: n } });
                  }}
                  className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-1 text-[12px] text-gray-900 dark:text-gray-50"
                />
                <input
                  defaultValue={f.flag ?? ''}
                  placeholder="FLAG"
                  onBlur={(e) => { if (e.target.value !== (f.flag ?? '')) patch.mutate({ id: f.id, campos: { flag: e.target.value } }); }}
                  className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-1 font-mono text-[12px] text-gray-900 dark:text-gray-50"
                />
              </div>
              <textarea
                defaultValue={f.nota ?? ''}
                placeholder="nota (o que já foi provado, o que falta)"
                rows={2}
                onBlur={(e) => { if (e.target.value !== (f.nota ?? '')) patch.mutate({ id: f.id, campos: { nota: e.target.value } }); }}
                className="mt-2 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-1.5 py-1 text-[12px] text-gray-900 dark:text-gray-50"
              />
              {f.pid && f.flag ? (
                <div className="mt-1 flex items-center gap-1.5">
                  <code className="min-w-0 flex-1 truncate rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[11px] text-gray-800 dark:text-gray-100">
                    {`cd /opt/calcfin && venv/bin/python3.12 -m eval.jfix.jfix ciclo ${f.pid} --flag ${f.flag}`}
                  </code>
                  <CopyBtn texto={`cd /opt/calcfin && venv/bin/python3.12 -m eval.jfix.jfix ciclo ${f.pid} --flag ${f.flag}`} />
                </div>
              ) : null}
            </div>
          ))}
          {d.frentes.length === 0 ? (
            <Text className="text-[12px] text-gray-500 dark:text-gray-400">nenhuma frente aberta — escolha uma célula do placar.</Text>
          ) : null}
        </div>
      </Card>

      {/* ---------------------------------------------------------------- smells */}
      <Card>
        <div className="flex items-baseline justify-between gap-2">
          <Text className="font-semibold text-gray-900 dark:text-gray-50">
            Smells candidatos a regra determinística (≥{d.smells.minimo ?? 3} casos) — {fmtInt(d.smells.n)}
          </Text>
          <Text className="text-[11px] text-gray-500 dark:text-gray-400">{d.smells.arquivo_gerado_em || '—'}</Text>
        </div>
        <Text className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          O que repete 3× não precisa de LLM para ser visto. Cada candidata ainda exige patch gated OFF → guard_shadow na classe → gate golden5 → flip.
        </Text>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-2">regra sugerida</th>
                <th className="py-1 pr-2">fase × verba</th>
                <th className="py-1 pr-2 text-right">casos</th>
                <th className="py-1 pr-2 text-right">R$Δ</th>
                <th className="py-1 pr-2">condição em palavras</th>
              </tr>
            </thead>
            <tbody>
              {d.smells.candidatas.map((s) => (
                <tr key={s.regra} className="border-b border-gray-100 dark:border-gray-800 align-top">
                  <td className="py-1 pr-2"><code className="text-[11px] text-gray-900 dark:text-gray-50">{s.regra}</code></td>
                  <td className="py-1 pr-2 text-gray-900 dark:text-gray-50">{s.estagio} × <code className="text-[12px]">{s.verba}</code></td>
                  <td className="py-1 pr-2 text-right font-semibold tabular-nums text-gray-900 dark:text-gray-50">{fmtInt(s.n_casos)}</td>
                  <td className="py-1 pr-2 text-right tabular-nums text-gray-900 dark:text-gray-50">{s.r_delta ? fmtBRL(s.r_delta) : '—'}</td>
                  <td className="py-1 pr-2 text-[12px] text-gray-700 dark:text-gray-200">{s.condicao}</td>
                </tr>
              ))}
              {d.smells.candidatas.length === 0 ? (
                <tr><td colSpan={5} className="py-3 text-center text-gray-500 dark:text-gray-400">nenhuma candidata{d.smells.erro ? ` (${d.smells.erro})` : ''}</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>

      {sel ? (
        <>
          <div className="fixed inset-0 z-30 bg-black/20" onClick={() => setSel(null)} />
          <PainelCelula c={sel} onFechar={() => setSel(null)} onEscolher={(c) => criar.mutate(c)} criando={criar.isPending} />
        </>
      ) : null}
    </div>
  );
}
